import { BlogCta } from "@/components/blog/BlogCta";
import { BlogPostSeo } from "@/components/blog/BlogSeo";
import { PostBody } from "@/components/blog/PostBody";
import { PostNotFound } from "@/components/blog/PostNotFound";
import { PostPageShell, PostPageSkeleton } from "@/components/blog/PostPageShell";
import { PostToc } from "@/components/blog/PostToc";
import { ReadingProgress } from "@/components/blog/ReadingProgress";
import { RelatedPosts } from "@/components/blog/RelatedPosts";
import { ShareButtons } from "@/components/blog/ShareButtons";
import { blogAuthorPath, PublicRoutes } from "@/constants/routes";
import { Badge, Divider, Heading, Text } from "@/design-system";
import { useScrollDepth } from "@/hooks";
import { startPostTracking, trackBlogEvent } from "@/lib/blogAnalytics";
import { hydrateCalculators } from "@/lib/blogCalculators";
import { interceptCtaLead } from "@/lib/blogCtaLead";
import { trackEvent } from "@/lib/dataLayer";
import { DemoFormModal } from "@/pages/landingPage/components";
import { usePublishedPost } from "@/hooks/blog";
import { DEFAULT_ORIGIN } from "@/shared/blog/seo";
import { slugify } from "@/shared/blog/slug";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Clock, Home } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useParams } from "react-router";

const formatDate = (iso: string | null): string =>
  iso ? format(new Date(iso), "dd/MM/yyyy", { locale: ptBR }) : "";

export const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data: post, isLoading } = usePublishedPost(slug);

  // O progresso e o índice medem o **artigo**, não o documento: contar rodapé,
  // "Continue lendo" e CTA faria a barra marcar 60% no fim do texto.
  const articleRef = useRef<HTMLElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  useScrollDepth(!isLoading && Boolean(post));

  const postSlug = post?.slug;
  const postId = post?.id;
  const contentHtml = post?.contentHtml;

  // Analytics first-party (E3.3). Mede tempo ATIVO por heartbeat com a aba
  // visível — convive com o `useScrollDepth` acima, que é outro sistema (GTM)
  // e alimenta os painéis de marketing já existentes.
  useEffect(() => {
    if (!postSlug) return;
    return startPostTracking({ slug: postSlug, postId, container: bodyRef.current });
  }, [postSlug, postId]);

  // Calculadoras (E1.3): enriquecimento progressivo sobre o HTML que o SSR já
  // entregou. Depende de `contentHtml` e não só do slug — trocar de post por
  // navegação client-side reusa o componente sem remontá-lo.
  useEffect(() => {
    if (!contentHtml) return;
    return hydrateCalculators(bodyRef.current, { slug: postSlug, postId });
  }, [contentHtml, postSlug, postId]);

  // Botão do bloco `cta-lead` abre o formulário de demo no lugar de levar à LP
  // (ver `blogCtaLead.ts`). Mesma dependência das calculadoras: o HTML pode
  // trocar sem remontar o componente.
  // O cartão da lateral (`BlogCta`) abre o mesmo formulário.
  const [isDemoOpen, setIsDemoOpen] = useState(false);
  const openDemo = useCallback(
    (ctaLabel: "cta_lead" | "blog_cta") => {
      trackEvent({
        event: "cta_click_specialist",
        location: "blog_post",
        cta_label: ctaLabel,
        post_slug: postSlug,
      });
      // O botão do `cta-lead` já é contado pelo `startPostTracking` (ele fica
      // dentro do corpo do post); o da lateral fica fora do container.
      if (ctaLabel === "blog_cta" && postSlug) {
        trackBlogEvent(postSlug, "cta_click", { block_id: "blog-cta" }, postId);
      }
      setIsDemoOpen(true);
    },
    [postSlug, postId]
  );
  useEffect(() => {
    if (!contentHtml) return;
    return interceptCtaLead(bodyRef.current, () => openDemo("cta_lead"));
  }, [contentHtml, openDemo]);

  if (isLoading) return <PostPageSkeleton />;
  // Com o post em mãos (vindo do SSR ou de uma busca anterior), uma falha na
  // revalidação em segundo plano NÃO derruba a página: trocar o post pronto
  // pela tela de "não encontrado" é exatamente o soft 404 que o Google pune.
  if (!post) return <PostNotFound />;

  const url = `${DEFAULT_ORIGIN}/blog/${post.slug}`;
  // Sem `authorSlug` no contrato, o slug sai do nome — é a mesma regra que a
  // página de autor usa para reencontrar os posts. Ver `BlogAuthorPage`.
  const authorSlug = post.authorName ? slugify(post.authorName) : null;

  return (
    <>
      <BlogPostSeo post={post} />
      <ReadingProgress targetRef={articleRef} />
      <PostPageShell>
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_320px] lg:gap-10">
          <div className="mx-auto w-full max-w-3xl lg:mx-0 lg:max-w-none">
            <nav aria-label="Trilha de navegação" className="mb-6">
              <ol className="font-ds-sans text-ds-small text-ds-ink-600 flex flex-wrap items-center gap-1.5">
                <li>
                  <Link
                    to={PublicRoutes.HOME}
                    className="rounded-ds-sm hover:text-ds-brand-700 focus-visible:ring-ds-focus inline-flex cursor-pointer items-center gap-1 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                  >
                    <Home size={14} aria-hidden="true" />
                    Início
                  </Link>
                </li>
                <li aria-hidden="true">/</li>
                <li>
                  <Link
                    to={PublicRoutes.BLOG}
                    className="rounded-ds-sm hover:text-ds-brand-700 focus-visible:ring-ds-focus cursor-pointer focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                  >
                    Blog
                  </Link>
                </li>
                {post.category && (
                  <>
                    <li aria-hidden="true">/</li>
                    <li className="text-ds-ink-800" aria-current="page">
                      {post.category}
                    </li>
                  </>
                )}
              </ol>
            </nav>

            <article ref={articleRef}>
              <header className="flex flex-col gap-4">
                {post.category && (
                  <Badge tone="brand" shape="tag" size="sm" className="w-fit">
                    {post.category}
                  </Badge>
                )}
                <Heading level={1}>{post.title}</Heading>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
                  {post.authorName &&
                    (authorSlug ? (
                      <Text as="span" size="small" tone="subtle">
                        Por{" "}
                        <Link
                          to={blogAuthorPath(authorSlug)}
                          className="text-ds-brand-700 rounded-ds-sm focus-visible:ring-ds-focus cursor-pointer underline underline-offset-2 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none"
                        >
                          {post.authorName}
                        </Link>
                      </Text>
                    ) : (
                      <Text as="span" size="small" tone="subtle">
                        Por {post.authorName}
                      </Text>
                    ))}
                  {post.publishedAt && (
                    <Text as="span" size="small" tone="subtle" numeric>
                      <time dateTime={post.publishedAt}>{formatDate(post.publishedAt)}</time>
                    </Text>
                  )}
                  {post.readingTimeMin > 0 && (
                    <Text
                      as="span"
                      size="small"
                      tone="subtle"
                      className="inline-flex items-center gap-1"
                    >
                      <Clock size={14} aria-hidden="true" />
                      {post.readingTimeMin} min de leitura
                    </Text>
                  )}
                </div>
              </header>

              {/* Sem botões "Copiar para LLM"/"Ver como Markdown": IA que
                  pesquisa não clica em botão nem roda JavaScript. O que a leva
                  ao post é o HTML do SSR, o `<link rel="alternate"
                  type="text/markdown">` para /blog/{slug}.md, o /llms.txt e o
                  robots.txt liberando os agentes — tudo servido sem clique. */}

              {/* Em até 1023px o índice vem antes do texto, fechado, para não
                  empurrar o primeiro parágrafo para fora da primeira tela. A
                  partir de 1024px ele vive na coluna lateral, grudado. */}
              <PostToc
                bodyRef={bodyRef}
                html={post.contentHtml}
                className="mt-8 lg:hidden"
              />

              <div className="mt-8">
                <PostBody ref={bodyRef} html={post.contentHtml} />
              </div>

              <Divider className="mt-8" />
              <div className="pt-6">
                <ShareButtons url={url} title={post.title} />
              </div>
            </article>

            <div className="lg:hidden">
              <BlogCta onOpenDemo={() => openDemo("blog_cta")} />
            </div>

            <RelatedPosts
              currentSlug={post.slug}
              category={post.category}
              clusterId={post.clusterId}
            />
          </div>

          <aside aria-label="Índice e contato" className="hidden lg:block">
            <div className="flex flex-col gap-6 lg:sticky lg:top-28">
              <PostToc bodyRef={bodyRef} html={post.contentHtml} />
              <BlogCta onOpenDemo={() => openDemo("blog_cta")} />
            </div>
          </aside>
        </div>
      </PostPageShell>
      <DemoFormModal isOpen={isDemoOpen} onClose={() => setIsDemoOpen(false)} />
    </>
  );
};
